import React, { useState, useEffect, useCallback } from 'react';
import {  useSelector } from "react-redux";
import { Card, Typography, Row, Col, Button, Form, message, Upload, List, Tooltip, Divider, Spin } from 'antd';
import { UploadOutlined, DownloadOutlined, DeleteOutlined } from '@ant-design/icons';
import { BASE_URL } from '../Components/Constant';
import ChildRecordTable from '../Record/RecordTable';
import ApiService from '../Components/apiService';
import CreateRecordDrawer from '../Record/CreateRecordDrawer';
import { generateBody } from '../Components/Utility';
import FieldHistory from '../Record/FieldHistory';
import CreateRecordModal from '../Record/CreateRecordModal';
import isEqual from 'lodash/isEqual';
import { set } from 'lodash';
import { current } from '@reduxjs/toolkit';

const { Title } = Typography;

const RelatedList = ({ component, objectName, recordId, metadata, tableProperties, getRowStyle }) => {
  const [childRecordsMap, setChildRecordsMap] = useState([]);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [currentFieldsData, setCurrentFieldsData] = useState([]);
  const [form] = Form.useForm();
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentChildObjectName, setCurrentChildObjectName] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [layout, setLayout] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isRelatedCreate, setIsRelatedCreate] = useState(false);
  const [recordDetailsToSend, setRecordDetailsToSend] = useState({});
  const [isFieldReadOnly, setIsFieldReadOnly] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [relatedList, setRelatedList] = useState({});
  const [activeCurrencies, setActiveCurrencies] = useState([]);
  // const userCurrency = Cookies.get('userCurrency');
  const security = useSelector((state) => state.data.items[0]);


  useEffect(() => {
    console.log('component:', component?.highlightFilters);

    if (component.selectedRelatedList === 'FieldHistory') {
      setLoading(false);
    } else if (component.selectedRelatedList === 'Attachments') {
      fetchfiles();
    } else {
      fetchRelatedRecords().then(() => {
        if (component?.selectedRelatedList) {
          fetchChildRecords(
            component?.selectedRelatedList,
            recordId,
            currentPage,
            pageSize
          );
        } else {
          setLoading(false);
        }
      });
    }
  }, [component?.selectedRelatedList, objectName, recordId, currentPage, pageSize, component?.highlightFilters]);

  const fetchChildRecords = useCallback(
    async (relatedListId, recordId, page = currentPage, size = pageSize) => {
      setLoading(true);
      try {
        const offset = (page - 1) * size;
        const childRecordsService = new ApiService(
          `${BASE_URL}/related_lists/${relatedListId}/${recordId}/child_records?limit=${size}&offset=${offset}`,
          {},
          'GET'
        );
        const response = await childRecordsService.makeCall();
        setChildRecordsMap(Array.isArray(response.records) ? response.records : []);
        setTotalRecords(response.total || 0);
        setCurrentPage(page);
        setPageSize(size);
      } catch (err) {
        console.error(`Error fetching child records:`, err);
      } finally {
        setLoading(false);
      }
    },
    [currentPage, pageSize]
  );
  const handleFileUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file.file);
    try {
      // Assuming there is an API endpoint to handle file uploads
      const uploadService = new ApiService(`${BASE_URL}/file_upload/${objectName}/${recordId}`, {
        'Content-Type': "application/x-www-form-urlencoded"
      }, 'POST', formData);

      const response = await uploadService.makeCall();
      message.success('File uploaded succesfully');
      fetchfiles();
    } catch (error) {
      const errorMessage = error && typeof error === 'object'
        ? Object.entries(error).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`).join(' | ')
        : 'Failed to upload files due to an unknown error';
      message.error(errorMessage);
    }
  };

  const fetchfiles = async () => {
    setLoading(true);
    try {
      const apiService = new ApiService(
        `${BASE_URL}/get_data/${objectName}/${recordId}`,
        { 'Content-Type': 'application/json' },
        'GET',
      );
      const response = await apiService.makeCall();

      setFiles(response);
    } catch (error) {
      console.error('Error fetching files:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFile = async (fileId) => {

    try {
      const apiService = new ApiService(
        `${BASE_URL}/download_file/${fileId}`,
        { 'Content-Type': 'application/json' },
        'GET',
      );

      const response = await apiService.makeCall(); // Fetching the response

      if (response && response.content && response.content.$binary) {
        const { base64 } = response.content.$binary; // Get Base64-encoded content
        const { filename } = response; // Extract filename

        // Decode Base64 into a binary Blob
        const binaryData = atob(base64); // Decoding Base64
        const byteArray = new Uint8Array(binaryData.length);

        for (let i = 0; i < binaryData.length; i++) {
          byteArray[i] = binaryData.charCodeAt(i);
        }

        // Create a Blob and trigger the download
        const blob = new Blob([byteArray], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);

        // Create a temporary <a> element for the download
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'downloaded_file.csv'; // Use filename or default to 'downloaded_file.csv'
        document.body.appendChild(a);
        a.click();

        // Cleanup
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

      } else {
        throw new Error('Invalid file content received from the API.');
      }
    } catch (error) {
      const errorMessage = error && typeof error === 'object'
        ? Object.entries(error).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`).join(' | ')
        : 'Failed to download file due to an unknown error';
      message.error(errorMessage);
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      // Assuming there is an API endpoint to handle file uploads
      const apiService = new ApiService(
        `${BASE_URL}/delete_file/${fileId}`,
        { 'Content-Type': 'application/json' },
        'DELETE'
      );
      const response = await apiService.makeCall();
      message.success('File deleted succesfully');
      fetchfiles();
    } catch (error) {
      const errorMessage = error && typeof error === 'object'
        ? Object.entries(error).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`).join(' | ')
        : 'Failed to delete file due to an unknown error';
      message.error(errorMessage);
    }

  }

  const handleFilePreview = async (fileId) => {
    try {
      // Make an API call to fetch the file by its file_id
      const apiService = new ApiService(
        `${BASE_URL}/download_file/${fileId}`,
        { 'Content-Type': 'application/json' },
        'GET'
      );

      const response = await apiService.makeCall(); // Fetching the response

      // Extract base64 data from the response
      const base64Data = response?.content?.$binary?.base64;

      // Extract file type from the file name
      const fileName = response?.filename;
      const fileExtension = fileName?.split('.').pop()?.toLowerCase();

      if (!base64Data) {
        message.error('No file data available to preview');
        return;
      }

      // Handle preview based on file extension
      switch (fileExtension) {
        case 'pdf':
          previewPDF(base64Data);
          break;
        case 'docx':
        case 'doc':
          message.info('Preview not available for Word files. Please download to view.');
          break;
        case 'csv':
          message.info('Preview not available for CSV files. Please download to view.');
          break;
        case 'jpg':
        case 'jpeg':
        case 'png':
          previewImage(base64Data);
          break;
        default:
          message.error('Unsupported file type for preview');
          break;
      }
    } catch (error) {
      // Handle any errors during the process
      const errorMessage = error && typeof error === 'object'
        ? Object.entries(error).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`).join(' | ')
        : 'Failed to preview file due to an unknown error';
      message.error(errorMessage); // Show error message to the user
    }
  };

  const previewPDF = (base64Data) => {
    const binary = atob(base64Data); // Decode base64 data
    const byteArray = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      byteArray[i] = binary.charCodeAt(i);
    }

    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  const previewImage = (base64Data) => {
    const img = new Image();
    img.src = `data:image/jpeg;base64,${base64Data}`; // Set base64 as image source
    const newWindow = window.open();
    newWindow.document.body.appendChild(img);
  };

  const handleDrawerClose = () => {
    setIsRelatedCreate(false);
    if (metadata?.creationView === "Modal") {
      setModalVisible(false);
    }
    else {
      setIsDrawerVisible(false);
    }
    setCurrentFieldsData([]);
  };

  const handleFinish = async (values, saveAndNew = false) => {

    const updatedValues = generateBody(currentFieldsData, values, (selectedRecord?._id && !selectedRecord?.isClone) ? false : true);
    if (updatedValues === "error") {
      return;
    }
    const changedValues = {};

    if (selectedRecord?._id) {

      Object.keys(updatedValues).forEach((key) => {
        const newValue = updatedValues[key];
        const oldValue = selectedRecord[key];

        const isNewKey = !(key in selectedRecord);
        const isEmpty = newValue === "" || newValue === null || newValue === undefined;

        if (!isEqual(newValue, oldValue)) {
          if (!isNewKey || (isNewKey && !isEmpty)) {
            changedValues[key] = newValue;
          }
        }
      });

      // Remove keys with undefined values
      Object.keys(changedValues).forEach(key => {
        if (changedValues[key] === undefined) {
          delete changedValues[key];
        }
      });
    }



    const body = {
      object_name: currentChildObjectName,
      data: {
        _id: selectedRecord?._id && !selectedRecord?.isClone ? selectedRecord._id : undefined, // If cloning, exclude the ID
        ...((selectedRecord?.isClone || !selectedRecord?._id) ? updatedValues : changedValues)
      }
    };


    try {
      const apiService = new ApiService(
        `${BASE_URL}/insert_or_update_records`,
        { 'Content-Type': 'application/json' },
        'POST',
        body
      );
      await apiService.makeCall();
      message.success(selectedRecord?._id && !selectedRecord?.isClone ? 'Record updated successfully' : 'Record created successfully');
      if (!saveAndNew) {
        if (metadata?.creationView === "Modal") {
          setModalVisible(false);
        }
        else {
          setIsDrawerVisible(false);
        }
        form.resetFields();
      } else {
        form.resetFields();
      }
      fetchChildRecords(
        component?.selectedRelatedList,
        recordId,
        currentPage,
        pageSize
      );
      // form.resetFields();
    } catch (error) {
      console.error('Error saving record:', error);
      const errorMessage = error && typeof error === 'object'
        ? Object.entries(error).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`).join(' | ')
        : 'Failed to create record due to an unknown error';
      message.error(errorMessage);
    }
  };

  const fetchRelatedRecords = async () => {
    try {
      const relatedListService = new ApiService(
        `${BASE_URL}/related_lists/for_object/${objectName}`,
        {},
        'GET'
      );
      const response = await relatedListService.makeCall();
      console.log('Related Lists Response:', response);
      console.log('component.selectedRelatedList:', component?.selectedRelatedList);


      const rl = response.find(rl => rl?.related_list?._id === component?.selectedRelatedList);
      console.log('Selected Related List:', rl);

      setRelatedList(rl || {});
    } catch (err) {
      console.error('Error fetching related records', err);
    }
  };

  const handleNewButtonClick = async (relatedList) => {
    if (security?.object_settings?.[relatedList?.related_list?.child_object_name]?.object_setting.create === false) {
        message.error('You dont have permission to create ');
        return;
      }
    fetchCurrentRecordDetails();
    setCurrentChildObjectName(relatedList?.related_list?.child_object_name);
    const fields = await fetchFieldsForChildObject(relatedList?.related_list?.child_object_name);
    setIsRelatedCreate(true);
    if (metadata?.creationView === "Modal") {
      setModalVisible(true);
      const lookupfield = fields.filter((field) => field.type === 'lookup' && field.parent_object_name === objectName  && field.name=== relatedList?.related_list?.field_api_name);
      console.log('lookupfieldlookupfield:', lookupfield);
      console.log('recordDetailsToSendrecordDetailsToSend:', recordDetailsToSend);
      form.setFieldsValue({
        [lookupfield[0]?.name]: recordDetailsToSend,
      });

    }
    else {
      setIsDrawerVisible(true);
      const lookupfield = fields?.filter((field) => field.type === 'lookup' && field.parent_object_name === objectName && field.name=== relatedList?.related_list?.field_api_name);
      form.setFieldsValue({
        [lookupfield[0]?.name]: recordDetailsToSend,
      });

    }
  };

  const handleDeleteChildRecord = useCallback(
    async (childObjectName, childRecordId) => {
      try {
        const apiService = new ApiService(
          `${BASE_URL}/delete_record/${childObjectName}/${childRecordId}`,
          {},
          'DELETE'
        );
        await apiService.makeCall();
        message.success('Record deleted successfully');

        // Refresh child records after deletion
        fetchChildRecords(component?.selectedRelatedList, recordId);
      } catch (error) {
        console.error('Error deleting record:', error);
        message.error('Failed to delete record');
      }
    },
    [fetchChildRecords, component?.selectedRelatedList, recordId]
  );

  const refreshRecords = () => {
    fetchChildRecords(component?.selectedRelatedList, recordId);
  }

  const fetchCurrentRecordDetails = async () => {
    try {
      const recordService = new ApiService(`${BASE_URL}/fetch_single_record/${objectName}/${recordId}`, {}, 'GET');
      const response = await recordService.makeCall();
      setRecordDetailsToSend(response);

      return response;
    } catch (error) {
      console.error('Error fetching current record details:', error);
    }
  };

  const fetchCurrency = useCallback(async () => {
    try {
      setLoading(true);
      const apiService = new ApiService(`${BASE_URL}/mt_currencies`, {
        'Content-Type': 'application/json',
      }, 'GET');
      const response = await apiService.makeCall();
      console.log('responseresponse:', response);
      setActiveCurrencies(response);
    } catch (err) {
      console.error("Error fetching metadata:", err);
    } finally {
      setLoading(false);
    }
  }, []);
  const getConvertedValue = (amount, fromCode, toCode) => {
    const parsedAmount = parseFloat(amount);

    // Check for invalid or empty amount
    if (isNaN(parsedAmount) || fromCode === toCode) {
      return isNaN(parsedAmount) ? '0.00' : parsedAmount.toFixed(2);
    }
    const fromCurrency = activeCurrencies.find(c => c.iso_code === fromCode);
    const toCurrency = activeCurrencies.find(c => c.iso_code === toCode);
    console.log('activeCurrencies isss', activeCurrencies);
    console.log('fromCurrency', fromCurrency, toCurrency);
    if (!fromCurrency || !toCurrency || !fromCurrency.conversion_rate || !toCurrency.conversion_rate) {
      return '0.00';
    }

    const inCorporate = parsedAmount / fromCurrency.conversion_rate;
    const converted = inCorporate * toCurrency.conversion_rate;

    return converted.toFixed(2);
  };
  const fetchFieldsForChildObject = async () => {
    try {
      const fieldsService = new ApiService(
        `${BASE_URL}/mt_fields/object/${relatedList?.related_list?.child_object_name}`,
        {},
        'GET'
      );
      const response = await fieldsService.makeCall();
      setCurrentFieldsData(response);

      const layouts = new ApiService(`${BASE_URL}/get_by_objectName/${relatedList?.related_list?.child_object_name}`, {}, 'GET');

      const res = await layouts.makeCall();
      if (res) {
        const activeLayout = res.filter(layout => layout.active);
        setLayout(activeLayout);
      }
      return response ?? [];
    } catch (err) {
      console.error('Error fetching fields for child object:', err);
    }
  };

  if (loading) {
    return <Spin />;
  }

  switch (component.selectedRelatedList) {
    case 'FieldHistory':
      return (
        <Card style={{ marginTop: "10px" }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4} style={{ margin: 0 }}>
                Field History
              </Title>
            </Col>
          </Row>
          <Divider style={{ margin: '8px 0' }} />
          <FieldHistory />
        </Card>
      );
    case 'Attachments':
      return (
        <Card
          title={
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={4} style={{ margin: 0 }}>
                  Upload Files
                </Title>
              </Col>
              <Col>
                <Upload
                  customRequest={handleFileUpload} // Use custom file upload handler
                  showUploadList={false} // Hide default upload list
                >
                  <Button icon={<UploadOutlined />}>Upload Files</Button>
                </Upload>
              </Col>
            </Row>
          }
        >
          <List
            dataSource={files}
            renderItem={(file) => (
              <List.Item
                actions={[
                  <Tooltip title="Download file">
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownloadFile(file.file_id)}
                    />

                  </Tooltip>,
                  <Tooltip title="Delete file">
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteFile(file.file_id)} // Add your delete handler here
                      danger
                    />
                  </Tooltip>,


                ]}
              >
                <List.Item.Meta
                  title={
                    <a
                      href="#"
                      style={{
                        fontWeight: '500',
                        color: '#1F5FBF',
                        textDecoration: 'none', // Removes default underline
                        borderBottom: '1px solid #1F5FBF', // Adds bottom border
                        paddingBottom: '2px' // Optional, adds space for a cleaner look
                      }}
                      onClick={(e) => {
                        e.preventDefault(); // Prevent default anchor behavior
                        handleFilePreview(file.file_id); // Call preview function
                      }}
                    >
                      {file.file_name}
                    </a>
                  }
                  description=""
                />
              </List.Item>
            )}
          />
        </Card>
      );
    default:
      const relatedListNotFound = !relatedList || !relatedList.related_list;
      return (
        <>
          <div
            key={relatedList?.child_object_id || 'not-found'}
            style={{ marginBottom: 16, minHeight: '200px', backgroundColor: 'white', width: '100%', height: '100%', border: '1px solid #f0f0f0', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0px 24px',
                borderBottom: '1px solid #f0f0f0',
                background: '#fff',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                minHeight: 44,
                boxSizing: 'border-box',
                marginBottom: 0
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <Title level={4} style={{ margin: 0 }}>
                  {relatedListNotFound
                    ? "Related List Not Found"
                    : relatedList?.related_list?.related_list_name}
                </Title>
              </span>
              <Button
                type="primary"
                onClick={() => handleNewButtonClick(relatedList)}
                disabled={relatedListNotFound}
                style={{ padding: '8px 7px', height: '22px' }}
              >
                New
              </Button>
            </div>
            {relatedListNotFound ? (
              <div style={{ color: 'red', padding: '16px 0', fontWeight: 500, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px', textAlign: 'center' }}>
                The related list has been deleted.
              </div>
            ) : (
              <div style={{ padding: '8px' }}>
                <ChildRecordTable
                  fetchCurrency={fetchCurrency}
                  getConvertedValue={getConvertedValue}
                  fieldsToDisplay={relatedList?.related_list?.fields_to_display || []}
                  childRecords={childRecordsMap || []}
                  fieldsData={relatedList?.fields_data || []}
                  childObjectName={relatedList?.related_list?.child_object_name}
                  onDelete={handleDeleteChildRecord}
                  relatedListId={relatedList?.related_list?._id}
                  currentRecordId={recordId}
                  currentObjectName={objectName}
                  child_object_id={relatedList?.child_object_id}
                  creationView={metadata?.creationView}
                  layout={layout}
                  refreshRecords={refreshRecords}
                  pagination={{
                    current: currentPage || 1,
                    pageSize: pageSize || 3,
                    total: totalRecords || 0,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    onChange: (page, size) => {
                      fetchChildRecords(relatedList?.related_list?._id, recordId, page, size);
                      setCurrentPage(page);
                      setPageSize(size);
                    },
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`,
                  }}
                  highlightFilters={component?.highlightFilters || []}
                  filterLogic={component?.filterLogic || ''}
                  highlightTooltipMessage={component?.highlightMessage || ''}
                  tableProperties={tableProperties}
                  getRowStyle={getRowStyle}
                  component={component}
                />
              </div>
            )}
          </div>
          {isDrawerVisible && (
            <CreateRecordDrawer
              visible={isDrawerVisible}
              onClose={handleDrawerClose}
              onFinish={handleFinish}
              loading={false}
              fieldsData={currentFieldsData}
              objectName={currentChildObjectName}
              form={form}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              isRelatedCreate={isRelatedCreate}
              parent_object_name={objectName}
            />
          )}
          {modalVisible &&
            <CreateRecordModal
              visible={modalVisible}
              onClose={handleDrawerClose}
              onFinish={handleFinish}
              loading={false}
              objectName={currentChildObjectName}
              fieldsData={currentFieldsData}
              selectedRecord={selectedRecord}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              form={form}
              // layout={layout}
              isRelatedCreate={isRelatedCreate}
            />
          }
        </>
      );
  }
}

export default RelatedList
